import Image from 'next/image';
import React, { CSSProperties } from 'react';
import type { ToastContent, ToastOptions } from 'react-toastify';

import { ApiErrorResponse } from '@cineamo/api/types/ApiErrorResponse.types';

import Button from '../button/Button';
import { IconClose16 } from '../icons/ic-close';
import '../styles/toastify.css';

import { copyToClipboard } from './clipboard-helper';
import { isMobile } from './screen-type';

/**
 *
 * @param options
 */
export function showToast(options: {
    toastType: (content: ToastContent, options?: ToastOptions | undefined) => React.ReactText;
    /**
     * Stable id for this toast. Pass one when the call site can fire more than once for the
     * same event — typically a `useEffect` body, which React deliberately invokes twice on
     * mount under Strict Mode (on by default for the App Router unless `reactStrictMode` is
     * set). Without an id each call stacks another toast; with one, the effect's cleanup can
     * `toast.dismiss(id)` so only a single toast survives (CIN-14507).
     */
    toastId?: ToastOptions['toastId'];
    messageIcon?: string | React.JSX.Element;
    messageTitle?: string | React.JSX.Element;
    messageBody?: string | React.JSX.Element;
    messageButton?: React.JSX.Element;
    messageButtonStopPropagation?: boolean;
    backgroundColor?: string;
    textColor?: string;
    buttonColor?: React.JSX.Element;
    closeButton?: boolean | React.JSX.Element;
    onClose?: () => void;
    onClick?: () => void;
    autoClose?: number | false;
    closeOnClick?: boolean;
}) {
    const style: CSSProperties = {};

    const {
        toastType,
        toastId,
        messageIcon,
        messageTitle,
        messageBody,
        backgroundColor,
        textColor,
        closeButton,
        messageButton,
        messageButtonStopPropagation = true,
        onClose,
        onClick,
        autoClose,
        closeOnClick = true
    } = options;

    if (backgroundColor) {
        style.backgroundColor = backgroundColor;
    } else {
        style.backgroundColor = '#fff';
    }
    if (textColor) {
        style.color = textColor;
    } else {
        style.color = '#000023';
    }

    function onCloseInternal() {
        if (onClose) {
            onClose();
        }
    }

    function onClickInternal() {
        if (onClick) {
            onClick();
        }
    }

    const messageElement: React.JSX.Element = (
        <div className="flex flex-col space-y-16 px-2">
            <div className="flex flex-row space-x-16 items-center">
                {messageIcon && React.isValidElement(messageIcon) ? (
                    messageIcon
                ) : messageIcon && typeof messageIcon === 'string' ? (
                    <div className="w-32 h-32 relative my-auto justify-center cursor-text">
                        <Image
                            className="w-full h-full object-contain"
                            src={messageIcon}
                            width={100}
                            height={100}
                            alt=""
                        />
                    </div>
                ) : null}
                <div className="w-0 flex-auto flex flex-col space-y-4">
                    {messageTitle && <span className="font-montserrat-semibold">{messageTitle}</span>}
                    {messageBody && (
                        <span className="flex flex-col whitespace-pre-wrap overflow-auto w-full max-h-[50vh]">
                            {messageBody}
                        </span>
                    )}
                </div>
            </div>
            {messageButton &&
                (messageButtonStopPropagation ? (
                    <div
                        className="w-full flex flex-col"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}>
                        {messageButton}
                    </div>
                ) : (
                    <div
                        className="w-full flex flex-col"
                        style={{ pointerEvents: 'auto' }}>
                        {messageButton}
                    </div>
                ))}
        </div>
    );

    toastType(messageElement, {
        toastId: toastId,
        className: '',
        bodyClassName: 'font-montserrat-regular',
        style: { ...style, pointerEvents: 'auto' },
        position: isMobile ? 'top-center' : 'top-right',
        hideProgressBar: true,
        closeOnClick: closeOnClick,
        onClick: onClick ? onClickInternal : undefined,
        draggable: false,
        progress: undefined,
        onClose: onCloseInternal,
        autoClose:
            autoClose === false
                ? false
                : calculateReadingTime([messageTitle, messageBody].filter((it) => !!it).join(' ')),
        closeButton: closeButton,
        icon: false
    });
}

export function showErrorToast<TErrorResponse>({
    toastType,
    messageTitle,
    error,
    buttonLabel
}: {
    toastType: (content: ToastContent, options?: ToastOptions | undefined) => React.ReactText;
    messageTitle?: string | React.JSX.Element;
    error?: ApiErrorResponse<TErrorResponse> | Error | null;
    buttonLabel: string;
}) {
    // Handle different error types
    let errorBody: React.JSX.Element | null = null;
    let errorForClipboard: ApiErrorResponse<TErrorResponse> | Error = error;

    if (error instanceof Error) {
        // For JavaScript Error objects, show the message
        errorBody = <span className="italic">{error.message}</span>;
        errorForClipboard = {
            name: error.name,
            message: error.message,
            stack: error.stack
        };
    } else if (error?.data) {
        // For ApiErrorResponse objects, show the data
        errorBody = <span className="italic">{JSON.stringify(error.data)}</span>;
    }

    showToast({
        toastType: toastType,
        messageIcon: <IconClose16 />,
        messageTitle: messageTitle,
        messageBody: errorBody,
        messageButton: error ? (
            <Button onClick={() => copyToClipboard(JSON.stringify(errorForClipboard))}>{buttonLabel}</Button>
        ) : null
    });
}

function calculateReadingTime(text) {
    const wordsPerMinute = 150; // Average reading speed
    const words = text.trim().split(/\s+/).length; // Split text by spaces to count words
    const minutes = words / wordsPerMinute;
    const milliseconds = minutes * 60 * 1000; // Convert minutes to milliseconds

    return Math.round(Math.max(milliseconds, 3000)); // Round to the nearest millisecond
}
